<template>
  <!------------------------------------------
    This component displays the web version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-state-web-v" class="lsc-dot-gov-styling">
    <div v-if="loading">
      <LoadingSpinner />
    </div>
    <Block v-else>
      <br/><br/>
      <div id="st-profile-area-web" ref="profilearea" :class="$vuetify.breakpoint.smAndDown === true ? 'mobile' : ''" v-if="profileData !== undefined && profileData !== null">
        <!-- HEADER AREA -->
        <div class="grid-header">
          <h2 class="main-title">
            <span class="title-1">Civil legal needs & services in</span>
            <span class="title-2">{{ selectedStateName }}</span>
          </h2>
        </div>

        <div class="grid-download">
          <div id="one-pager-link">
            <v-icon
              color="#b31308"
              left
              class="one-pager-icon"
              size="30">
                {{ icons.download }}
            </v-icon>
            <a :href="onePagerBoxLinksByState[selectedState]" target="_parent">Download the one-pager</a></div>
        </div>

        <div class="grid-needs grid-section">
          <h3 class="with-left-border">Legal Needs</h3>
          
          <!-- JUSTICE GAP STATS -->
          <div class="grid-section-row" v-if="isTerritory === false">
            <ProfileDataRow title="From LSC's Justice Gap Study" icon="scale" columnsWhenWide="2">
              <ProfileDataPoint v-for="pnt in justiceGapPoints" icon="circle" :key="pnt.name" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" />
            </ProfileDataRow>
          </div>
          
          <!-- ELIGIBILITY & POVERTY -->
          <div class="grid-section-row">
            <ProfileDataRow title="LSC Eligibility" icon="people" :centered="false" columnsWhenWide="2">
              <ProfileDataPoint v-for="pnt in eligibilityPoints" :key="pnt.name" :icon="pnt.icon" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" :sideBySide="pnt.sideBySide" :afterText="pnt.afterText" />
            </ProfileDataRow>
          </div>
  
          <!-- HOUSING STATS -->
          <div class="grid-section-row" v-if="housingPoints.length > 0">
            <ProfileDataRow title="Housing Issues" icon="house" columnsWhenWide="2">
              <ProfileDataPoint v-for="pnt in housingPoints" icon="circle" :key="pnt.name" :introText="pnt.introText" :sideBySide="pnt.sideBySide" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" :comparisonDataPoint="pnt.comparisonDataPoint" :comparisonText="pnt.comparisonText" :inlinePoint="pnt.inlinePoint" :afterText="pnt.afterText" />
            </ProfileDataRow>
          </div>
        </div>

        <div class="grid-spacer"></div>

        <!-- GRANTEES -->
        <div class="grid-grantees grid-section">
          <h3 class="with-left-border">Grantees</h3>

          <StateProfilesWebGranteesList 
            :selectedState="selectedState"
            :granteesByType="granteesByType"
            :colorPerServArea="colorPerServArea"
            :officesPerGrantee="officesPerGrantee"
            @orgSelected="(e) => setFilterMapToOrg(e)"
            @orgDeselected="(e) => unfilterMapOrg(e)"
          />
        </div>

        <!-- MAP -->
        <div class="grid-map grid-section">
          <StateProfilesWebMap  v-if="$vuetify.breakpoint.smAndDown === false"
            :key="selectedState"
            :selectedState="selectedState"
            :colorPerServArea="colorPerServArea"
            :officesPerGrantee="officesPerGrantee"
            :filterMapToOrg="filterMapToOrg"
            :granteesByType="granteesByType"
          />
        </div>

        <div class="grid-spacer2"></div>

        <div class="grid-served grid-section">
          <h3 class="with-left-border">Services</h3>
          
          <!-- PEOPLE SERVED -->
          <div class="grid-section-row">
            <ProfileDataRow :title="'Served in ' + garYear" icon="handshake" :centered="false" columnsWhenWide="2">
              <ProfileDataPoint v-for="pnt in servedPoints" :key="pnt.name" :icon="pnt.icon" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" :sideBySide="pnt.sideBySide" />
            </ProfileDataRow>
          </div>

          <!-- CLOSED CASES -->
          <div class="grid-section-row">
            <ProfileDataRow title="Top Closed Cases" icon="gavel">
              <StateProfilesWebBarChart :caseData="casesPoints" v-if="$vuetify.breakpoint.smAndDown === false" :key="resizeWatch" />
              <div v-else class="cases-bars-mobile-alt">
                <ProfileDataPoint v-for="pnt in casesPoints" :key="pnt.name" :pointOnRight="true" :text="pnt.text" :italicText="pnt.italicText" :indentText="pnt.indentText" :dataPoint="pnt.dataPoint" :italicDataPoint="pnt.italicDataPoint" />
              </div>
            </ProfileDataRow>
          </div>

          <!-- FUNDING -->
          <div class="grid-section-row">
            <ProfileDataRow title="Funding" icon="dollar" columnsWhenWide="2">
              <ProfileDataPoint v-for="pnt in fundingPoints" icon="circle" :key="pnt.name" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" :sideBySide="pnt.sideBySide" />
            </ProfileDataRow>
          </div>
        </div>
      </div>

      <!-- SOURCES -->
      <div id="st-profile-sources">
        <v-expansion-panels accordion>
          <v-expansion-panel>
            <v-expansion-panel-header><h4>Sources</h4></v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="sources-category">
                <div class="sources-category-name">Eligibility</div>
                <div class="sources-category-value" v-if="isTerritoryBesidesPuertoRico">Island area population estimates not available in the American Community Survey are from the latest Decennial Census or other island area censuses.
                </div>                
                <div class="sources-category-value" v-else>{{ acsYear }} American Community Survey 1-year estimates: 
                  <ul>
                    <li>Table S1701 for percent of the total population below 125% poverty.</li>
                    <li>Table S1703 for percent of children and seniors below 125% poverty.</li>
                    <li v-if="includesVets">Table S2101 for percent of veterans below <i><b>100%</b></i> poverty.</li>
                    <li v-if="includesRural">Table S1701 for percent of rural residents below 125% poverty, where rural is "Not in metropolitan statistical area" as used by the <a href="https://www.ers.usda.gov/topics/rural-economy-population/rural-classifications/" target="_parent">USDA ERS</a>.</li>
                  </ul>
                </div>
              </div>
              <div class="sources-category" v-if="isTerritoryBesidesPuertoRico === false">
                <div class="sources-category-name">Justice Gap</div>
                <div class="sources-category-value"><a :href="justiceGapLink" target="_parent">Snapshot of Key Findings</a> for the state's region, 2022 Justice Gap Study
                </div>
              </div>
              <div class="sources-category" v-if="housingPoints.length > 0">
                <div class="sources-category-name">Housing</div>
                <div class="sources-category-value">
                  <ul>
                    <li v-if="includesRepresentation">Tenant and landlord representation rates are from the LSC's <a href="https://civilcourtdata.lsc.gov/" target="_parent">Civil Court Data Initiative</a></li>
                    <li v-if="includesFilingsRate">Rate of eviction filings per renter household is the highest observed among counties in the state for which data are collected in LSC's <a href="https://civilcourtdata.lsc.gov/" target="_parent">Civil Court Data Initiative</a>. </li>
                    <li v-if="includesRentBurden">Percent of renters with rent burden (paying > 30% of income on rent) is calculated from {{ acsYear }} American Community Survey 1-year estimates, table B25070 (excluding "Not computed").</li>
                    <li v-if="includesHrsPerWeek">Hours per week to afford a 1 bedrooom apartment is from the National Low-Income Housing Coalition's {{ nlihcOorYear }} <a href="https://nlihc.org/oor" target="_parent">Out of Reach</a> report.</li>
                    <li v-if="includesELIUnits">The number of units available and affordable per 100 households with extremely low incomes is from the National Low-Income Housing Coalition's {{ nlihcGapYear }} <a href="https://nlihc.org/gap" target="_parent">The Gap</a> report.</li>
                  </ul>
                </div>
              </div>
              <div class="sources-category">
                <div class="sources-category-name">People served</div>
                <div class="sources-category-value">Reflects people served in handled cases (closed and open cases), from LSC's {{ garYear }} Grantee Activity Reports. To provide a more complete picture of the work of LSC grantees, this methodology was updated in 2025 to reflect people served in cases handled instead of cases closed only. LSC grantees also reach millions of people through community legal education and outreach that are not reflected in these numbers.
                  <ul>
                    <li>People served in handled cases is calculated as the number of handled cases multiplied by the national average household size of closed cases.</li>
                    <li>Veterans served in handled cases is calculated as the number of cases closed with a veteran in the household multiplied by the national ratio of handled to closed cases.</li>
                    <li>Older Americans served in handled cases is calculated as the number of clients 60 and over in closed cases multiplied by the national average number of adults per closed case and the national ratio of handled to closed cases.</li>
                    <li>Domestic violence survivors served in handled cases is calculated as the number of closed cases involving domestic violence multiplied by the national ratio of handled to closed cases.</li>
                  </ul>
                </div>
              </div>
              <div class="sources-category">
                <div class="sources-category-name">Cases closed</div>
                <div class="sources-category-value">From LSC's {{ garYear }} Grantee Activity Reports. Legal problem categories are not displayed where the number of closed cases was less than 2% of the total, or for miscellaneous categories (e.g., "Other Housing").
                </div>
              </div>
              <div class="sources-category">
                <div class="sources-category-name">Funding</div>
                <div class="sources-category-value">LSC grant awards, {{ profileData.funding.special_grants.since_year }} to present.
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </Block>
  </div>
</template>

<script>
import Block from '@/components/layout/Block'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import ProfileDataRow from '@/components/st_cd_snapshots/ProfileDataRow'
import ProfileDataPoint from '@/components/st_cd_snapshots/ProfileDataPoint'
import StateProfilesWebMap from '@/views/advocacy/congress/state_profiles/StateProfilesWebMap'
import StateProfilesWebGranteesList from '@/views/advocacy/congress/state_profiles/StateProfilesWebGranteesList'
import StateProfilesWebBarChart from '@/views/advocacy/congress/state_profiles/StateProfilesWebBarChart'
import states from '@/js/stFips.js'
import { mdiFileDownloadOutline } from '@mdi/js';
import { unique_colors } from '@/js/map_utils.js'
import { problemCodes, categoryRename, codesPerCategory } from '@/js/problem_codes.js'
import { formatThousandsNoDecimal, percentToOneInX, formatPercentNoDecimalIfExists } from "@/js/number_formatters.js";
import snapshotLinks from '@/js/snapshots_box_links.js'

export default {
  name: 'StateProfilesWeb',

  components: { Block, ProfileDataRow, ProfileDataPoint, LoadingSpinner, StateProfilesWebMap, StateProfilesWebGranteesList, StateProfilesWebBarChart },

  props: {
    profileData: { type: Object, required: true },
    selectedState: { type: String, required: true },
    isTerritory: { type: Boolean, required: true },
    isTerritoryBesidesPuertoRico: { type: Boolean, required: true },
    granteesByType: { type: Object, required: true },
    officesPerGrantee: { type: Object, required: true }
  },

  data () {
    return {
      loading: true,
      resizeWatch: 0,
      debounceTimer: 0,
      garYear: null,
      acsYear: null,
      nlihcOorYear: null,
      nlihcGapYear: null,
      eligibilityPoints: [],
      justiceGapPoints: [],
      housingPoints: [],
      servedPoints: [],
      casesPoints: [],
      fundingPoints: [],
      colors: unique_colors,
      filterMapToOrg: null,
      includesVets: false,
      includesRural: false,
      includesRepresentation: false,
      includesFilingsRate: false,
      includesRentBurden: false,
      includesHrsPerWeek: false,
      includesELIUnits: false,
      justiceGapLink: 'https://justicegap.lsc.gov/resource/northeast/',
      icons: { download: mdiFileDownloadOutline },
      onePagerBoxLinksByState: snapshotLinks
    }
  },

  computed: {
    selectedStateName () { 
      if (this.selectedState !== '' && this.selectedState !== null) {
        return states.filter(x => x.fips === String(this.selectedState))[0].state 
      } else {
        return ''
      }
    },
    colorPerServArea () {
      let bfServAreas = this.granteesByType.bfGeneral.map(x => ({ area: x.serv_area, recipient_id: x.recipient_id }))
      let c = this.colors
      let colorMap = {}
      let colorIndex = 0

      return bfServAreas.map(function(x) {
      if (!colorMap[x.recipient_id]) {
        colorMap[x.recipient_id] = c[colorIndex % c.length]
        colorIndex++
      }
      return { area: x.area, color: colorMap[x.recipient_id] }
      })
    }
  },

  created () {
    this.parseProfileData()
  },

  mounted () {
    this.initObserver()
  },
  
  beforeDestroy() {
    // Unobserve resize
    if (this.resizeObserver) this.resizeObserver.unobserve(this.$refs.profilearea)
  },

  methods: {
    async parseProfileData () {
      this.loading = true
      this.garYear = this.profileData.gar_year
      this.acsYear = this.profileData.population.acs_year
      this.setEligibilityPoints(this.profileData)
      this.setJusticeGapPoints(this.profileData)
      this.setHousingPoints(this.profileData)
      this.setServedPoints(this.profileData)
      this.setFundingPoints(this.profileData)
      this.setCasesPoints(this.profileData)
      this.loading = false
    },

    setEligibilityPoints (d) {
      /**
       * Takes the data points in the fetched JSON file and orders them for display based on the business logic below (i.e. display the most compelling data points).
       * 
       * The function builds arrays of objects, where each object is a data point for display, "name" is a unique name for use in the v-for :key (and for readability), and the other properties are what will get passed to the ProfileDataPoint component.
       * 
       * @param {object}  d   Object from parsing the fetched JSON file from getProfileData()
       * @return  No return, but updates the reactive data.
       */

      let points = []

      points.push(
        { name: 'eligible people', icon: "people", dataPoint: `${percentToOneInX(d.population.pct_eligible)}`, text: "people", sideBySide: true }
      )

      if (!this.isTerritoryBesidesPuertoRico) {
        points.push(
          { name: 'eligible children', icon: "child", dataPoint: `${percentToOneInX(d.population.pct_children_in_elig_hh)}`, text: "children", sideBySide: true },
          { name: 'eligible seniors', icon: "seniors", dataPoint: `${percentToOneInX(d.population.seniors_eligible)}`, text: "older Americans", sideBySide: true }
        )
      }

      if (d.population.vet_pov_pct !== null && d.population.vet_pov_pct > 0.067) {
        points.push(
          { name: 'vet eligible', icon: "star", dataPoint: `${percentToOneInX(d.population.vet_pov_pct)}`, text: "veterans",  sideBySide: true }
        )
        this.includesVets = true
      }

      if (d.population.rural_eligible !== null) {
        points.push(
          { name: 'rural eligible', icon: "rural", dataPoint: `${percentToOneInX(d.population.rural_eligible)}`, text: "rural residents", sideBySide: true }
        )
        this.includesRural = true
      }

      this.eligibilityPoints = points
      return
    },

    setJusticeGapPoints (d) {
      /**
       * Same as setEligibilityPoints() but for the Justice Gap data section. See intro to setEligibilityPoints().
       */
      let points = []
      let region = d.justice_gap.region ? d.justice_gap.region.toLowerCase() : ''

      let stateApostropheS = this.selectedStateName.charAt(this.selectedStateName.length - 1) === 's' ? '' : 's'
      points.push(
        { name: 'Justice Gap % of problems unhelped', dataPoint: `Only ${formatPercentNoDecimalIfExists(d.justice_gap.problems_pct_unhelped)}`, text: `of the civil legal problems facing ${this.selectedStateName}'${stateApostropheS} low-income residents receive enough legal help.` },
        { name: 'Justice Gap % of issues to grantees unhelped', dataPoint: `Only ${formatPercentNoDecimalIfExists(d.justice_gap.grantees_pct_unhelped)}`, text: `of issues brought to LSC grantees in ${region}ern states receive enough help.` }
      )
      this.justiceGapPoints = points
      this.justiceGapLink = `https://justicegap.lsc.gov/resource/${region}/`
      return
    },

    setHousingPoints (d) {
      /**
       * Same as setEligibilityPoints() but for the Justice Gap data section. See intro to setEligibilityPoints().
       */

      let points = []

      // Representation rates
      if (d.housing.eviction_representation !== null && "tenant" in d.housing.eviction_representation && "landlord" in d.housing.eviction_representation && (d.housing.eviction_representation.landlord - d.housing.eviction_representation.tenant > 0.4 )) {
        points.push(
          { name: 'Eviction filing representation rates', dataPoint: `${formatPercentNoDecimalIfExists(d.housing.eviction_representation.tenant)}`, text: "of tenants have representation in court, compared to", comparisonDataPoint: `${formatPercentNoDecimalIfExists(d.housing.eviction_representation.landlord)}`, afterText: "of landlords.", inlinePoint: true }
        )
        this.includesRepresentation = true
      }

      // Eviction filing rate
      if (d.housing.max_county_eviction_filings_rate !== null && d.housing.max_county_eviction_filings_rate.rate > 0.1) {
        points.push(
          { name: 'Max filings rate', introText: `The rate of eviction filings in ${this.selectedStateName} counties is as high as`, dataPoint: `${percentToOneInX(d.housing.max_county_eviction_filings_rate.rate)}`, text: 'renter households.', inlinePoint: true }
        )
        this.includesFilingsRate = true
      }

      // Percent rental cost burdened
      if (d.housing.pct_rent_burdened !== null) {
        points.push(
          { name: 'Rental cost burdened', dataPoint: `${formatPercentNoDecimalIfExists(d.housing.pct_rent_burdened)}`, afterText: 'of renter households pay more than 30% of income on rent.', inlinePoint: true  }
        )
        this.includesRentBurden = true
      }

      // Work hours to afford 1 bedroom at FMR -- if above 65 hours
      if (d.housing.nlihc !== null && "hrs_wk_for_1br_fmr" in d.housing.nlihc && d.housing.nlihc.hrs_wk_for_1br_fmr !== null && d.housing.nlihc.hrs_wk_for_1br_fmr > 65) {
        points.push(
          { name: 'NLIHC hours of work', dataPoint: `${String(d.housing.nlihc.hrs_wk_for_1br_fmr)} hours/week`, afterText: 'at minimum wage to afford a 1-bedroom unit at Fair Market Rent.', inlinePoint: true  }
        )
        this.includesHrsPerWeek = true
        this.nlihcOorYear = d.housing.nlihc.oor_report_year
      }

      // Units per ELI renter HH
      if (d.housing.nlihc !== null && "units_per_eli" in d.housing.nlihc && d.housing.nlihc.units_per_eli !== null && d.housing.nlihc.units_per_eli < 65) {
        points.push(
          { name: 'NLIHC units per ELI', dataPoint: `Only ${String(d.housing.nlihc.units_per_eli)}`,text: "affordable units are available for every 100 extremely low-income renter households.", inlinePoint: true}
        )
        this.includesELIUnits = true
        this.nlihcGapYear = d.housing.nlihc.gap_report_year
      }

      this.housingPoints = points
      return
    },

    setServedPoints (d) {
      /**
       * Same as setEligibilityPoints() but for the Served data section. See intro to setEligibilityPoints().
       */

      let points = []

      points.push(
        { name: 'People served', icon: "people", dataPoint: `${formatThousandsNoDecimal(d.served.people)}`, text: "people", sideBySide: true },
        { name: 'Veterans served', icon: "star", dataPoint: `${formatThousandsNoDecimal(d.served.veterans)}`, text: "veterans", sideBySide: true },
        { name: 'Seniors served', icon: "seniors", dataPoint: `${formatThousandsNoDecimal(d.served.seniors)}`, text: "older Americans", sideBySide: true },
        { name: 'DV served', icon: "dv", dataPoint: `${formatThousandsNoDecimal(d.served.dv)}`, text: "domestic violence survivors", sideBySide: true }
      )
      this.servedPoints = points
      return
    },

    setFundingPoints (d) {
      /**
       * Same as setEligibilityPoints() but for the Served data section. See intro to setEligibilityPoints().
       */

      let points = []

      const bfVars = {'bf_general': 'to serve the eligible population', 'ag_worker': 'to serve agricultural workers', 'native_am': 'to serve Native Americans'}

      for (const x of Object.keys(bfVars)) {
        if (x in d.funding.basic_field && d.funding.basic_field[x] !== null && d.funding.basic_field[x] > 0) {
          points.push(
            { name: `${x} funding`, dataPoint: `$${formatThousandsNoDecimal(d.funding.basic_field[x])}`, text: `in ${d.funding.funding_year} ${bfVars[x]}`, sideBySide: true }
          )
        }        
      }

      // Then special grants
      const sgVars = {'disaster': 'to respond to disasters', 'pbif': 'for pro bono innovation', 'tig': 'for technology innovation'}

      for (const x of Object.keys(sgVars)) {
        if (x in d.funding.special_grants && d.funding.special_grants[x] !== null && d.funding.special_grants[x] > 0) {
          points.push(
            { name: `${x} funding`, dataPoint: `$${formatThousandsNoDecimal(d.funding.special_grants[x])}`, text: `since ${d.funding.special_grants.since_year} ${sgVars[x]}`, sideBySide: true }
          )
        }        
      }

      this.fundingPoints = points
      return
    },

    setCasesPoints (d) {
      /**
       * Same as setEligibilityPoints() but for the Cases Closed data section. See intro to setEligibilityPoints().
       * Creates the list of problem categories and percentages.
       */

      let points = []

      // Convert categories and codes to new array of objects for sorting 
      let categories = this.keyValuesToSortedArrayOfObjects(d.closed_cases.categories)
      let codes = this.keyValuesToSortedArrayOfObjects(d.closed_cases.codes_over_10_pct)
    
      // Exclude the "other_" codes from each category
      const codesToExclude = ['09', '19', '29', '39', '49', '59', '69', '79', '89', '99']
      const miscCodes = ['90','91','92','93','94','95','96','97','98']

      // Treat problem codes under "Miscellaneous" as if they were problem categories
      for (let x of codes) {
        if (miscCodes.indexOf(x.key) > -1) {
          categories.push(x)
        }
      }

      categories.sort((a, b) => parseFloat(b.value) - parseFloat(a.value));

      // Add each problem category (and its sub-codes) in order for the list of data points
      for (const c of categories) {
        if (c.value > 0.02 && categoryRename[c.key] !== 'Miscellaneous' && categoryRename[c.key] !== 'Misc') {
          let name = miscCodes.indexOf(c.key) > -1 ? problemCodes.filter(x => x.code === parseInt(c.key))[0].shortName : categoryRename[c.key]

          points.push(
            { name: `${c.key} cases`, text: name, dataPoint: `${formatPercentNoDecimalIfExists(c.value)}`,  italicText: false, indentText: false, italicDataPoint: false, origData: c.value }
          )
        }

        // Show any codes within this category if > 10% of all cases closed
        if (miscCodes.indexOf(c.key) > -1 || c.key === 'misc') { continue }

        const codesForThisCategory = codes.filter(x => codesPerCategory[c.key].indexOf(parseInt(x.key)) > -1)
        
        if (codesForThisCategory.length > 0) {
          for (const co of codesForThisCategory) {
            if (co.value > 0.1 && codesToExclude.indexOf(co.key) === -1) {
              points.push(
                { name: `${co.key} cases`, text: problemCodes.filter(x => x.code === parseInt(co.key))[0].shortName, dataPoint: `${formatPercentNoDecimalIfExists(co.value)}`, italicText: true, indentText: true, italicDataPoint: true, origData: co.value }
              )
            }
          }
        }
      }
      this.casesPoints = points
      return
    },

    keyValuesToSortedArrayOfObjects(obj) {
      /**
       * Convert an object like { key1: value1, key2: value2 } to an array of objects 
       * like [{ key: key1, value: value1 }, { key: key2, value: value2 }]
       * @param {object}  obj Object like { key1: value1, key2: value2 }
       * @return {array}  Returns array of objects
       */
      return Object.entries(obj).map(([key, value]) => ({key, value})).sort((a, b) => b.value - a.value)
    },

    setFilterMapToOrg(rno) {
      this.filterMapToOrg = rno
    },

    unfilterMapOrg() {
      this.filterMapToOrg = null
    },

        
    initObserver() {
      const observer = new ResizeObserver(this.onResize)
      observer.observe(this.$refs.profilearea)
      this.resizeObserver = observer
    },
    onResize() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {this.resizeWatch +=1}, 200);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/vars.scss';
$spacer-height: 5rem;

#st-profile-area-web {
  padding: 0;
  display: grid;
  grid-template-areas:
    'header header header'
    'download download download'
    'needs needs needs'
    'spacer spacer spacer'
    'grantees map map'
    'spacer2 spacer2 spacer2'
    'served served served'
    'cases cases cases'
    'funding funding funding';
  gap: 1rem;
  grid-auto-columns: 1fr;
  margin: 0 auto;

  .grid-header {
    grid-area: header;
    display: flex;
    justify-content: center;

    .main-title {
      display: flex;
      flex-direction: column;
      justify-content: flex-start; 
      text-align: center;
      line-height: 2rem;
      padding: 0;

      .title-1 {
        color: #404040;
        letter-spacing: 0.2rem;
        font-family: 'Lato', sans-serif;
        font-size: 1.25rem;
        font-weight: normal;
        margin-bottom: 0.5rem;
      }
      .title-2 {
        font-size: 4rem;
        line-height: normal;
        margin-bottom: 0;
      }
    }
  }
  .grid-download {
    grid-area: download;
    margin-bottom: 0.5rem;
    text-align: center;
  }
  .grid-needs {
    grid-area: needs;
  }
  .grid-map {
    grid-area: map;
    width: 100%;
  }
  .grid-grantees {
    grid-area: grantees;
  }
  .grid-served {
    grid-area: served;
  }
  .grid-needs, .grid-served {
    padding: 1rem;
    border-radius: 0.625rem;
    background-color: #daeefd;
  }
  .grid-spacer {
    grid-area: spacer;
    height: $spacer-height;
  }
  .grid-spacer2 {
    grid-area: spacer2;
    height: $spacer-height;
  }
  .grid-section-row {
    margin-bottom: 1rem;
  }
  .grid-section h3 {
    margin: 1rem 0 2rem 1rem;
  }
}
#st-profile-sources {
  margin-top: 5rem;

  .v-expansion-panels {
    border: 1px solid #e0eefa;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    background-color: #f0f7fc;
  }
  .v-expansion-panel {
    background-color: inherit;
  }
  .v-expansion-panel::before {
    box-shadow: none;
  }
  .v-expansion-panel-header {
    color: $header-red;
    letter-spacing: 0.2rem;
    font-weight: 400;
  }
  .sources-category {
    display: flex;
    font-size: 0.95rem;
    margin-bottom: 1rem;
    flex-wrap: nowrap;
    
    .sources-category-name {
      width: 8rem;
      min-width: 8rem;
      color: $main-blue;
      padding-right: 2rem;
    }
  }
}
#st-profile-area-web {
  &.mobile {
    .main-title {
      .title-1 {
        font-size: 1rem;
      }
      .title-2 {
        font-size: 2rem;
      }
    }
  }
  .cases-bars-mobile-alt {
    max-width: 35rem;
  }
}
@media screen and (max-width: 800px) {
  #st-profile-area-web {
      padding: 0 0.125rem;
  }
  #st-profile-sources .sources-category {
    flex-wrap: wrap;
  }
}
@media screen and (max-width: 1000px) {
  #st-profile-area-web {
    .grid-section {
      padding-left: 0.25rem;
      padding-right: 0.125rem;
    }
    grid-template-areas:
      'header'
      'download'
      'needs'
      'spacer'
      'grantees'
      'map'
      'spacer2'
      'served';
  }
}
</style>